export const WEIGHT_STEP_KG = 0.5;
export const WEIGHT_MIN_KG = 0.01;
export const WEIGHT_MAX_KG = 20;

export function roundWeightKg(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Plus or minus half a kilo. Null when the next step would leave 0.01–20 kg. */
export function stepWeightKg(current: number, direction: -1 | 1): number | null {
  const next = roundWeightKg(current + direction * WEIGHT_STEP_KG);
  if (next < WEIGHT_MIN_KG || next > WEIGHT_MAX_KG) return null;
  return next;
}

export function parseWeightKg(raw: string): number | null {
  const normalized = raw.trim().replace(',', '.');
  if (!normalized) return null;
  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  const rounded = roundWeightKg(value);
  if (rounded < WEIGHT_MIN_KG || rounded > WEIGHT_MAX_KG) return null;
  return rounded;
}

export function formatWeightKg(quantity: number): string {
  const rounded = roundWeightKg(quantity);
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(2).replace(/\.?0+$/, '');
}

export function unitsPerKilogram(unitOfMeasure: string | null | undefined): number {
  const text = (unitOfMeasure ?? '')
    .trim()
    .toLowerCase()
    .replace(/["״'׳`]/g, '');
  if (!text) return 1;
  if (isKilogram(text)) {
    const basis = leadingNumber(text);
    return basis > 0 ? 1 / basis : 1;
  }
  if (isGram(text)) {
    const basis = leadingNumber(text);
    return 1000 / (basis > 0 ? basis : 1);
  }
  return 1;
}

export function priceUnitsForKg(kilograms: number, unitOfMeasure: string | null | undefined): number {
  return Math.round(kilograms * unitsPerKilogram(unitOfMeasure) * 10000) / 10000;
}

export function lineTotal(
  pay: number,
  quantity: number,
  soldByWeight: boolean,
  unitOfMeasure: string | null | undefined
): number {
  const units = soldByWeight ? priceUnitsForKg(quantity, unitOfMeasure) : quantity;
  return Math.round(pay * units * 100) / 100;
}

export function cartItemCount(needs: ReadonlyArray<{ quantity: number; soldByWeight?: boolean }>): number {
  const sum = needs.reduce(
    (total, need) => total + (need.soldByWeight ? 1 : Number(need.quantity) || 0),
    0
  );
  return Number.isInteger(sum) ? sum : Math.round(sum * 100) / 100;
}

function isKilogram(text: string): boolean {
  return text.includes('קג') || text.includes('קילו') || text.includes('kg');
}

function isGram(text: string): boolean {
  return text.includes('גרם') || text.includes('גר') || text.includes('gram') || text.endsWith('g');
}

function leadingNumber(text: string): number {
  const match = text.match(/^\s*(\d+(?:\.\d+)?)/);
  if (!match) return 0;
  return Number(match[1]);
}
